import {
  Component,
  ElementRef,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';

import { Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { camelCase } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { v4 as UUIDv4 } from 'uuid';

import { DocumentViewOptions } from 'app/modules/collection/components/document/document-view/document-view.component';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { EvaluationService } from 'app/providers/semantria-api/evaluation-service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { DocumentAnalysis } from 'app/providers/storage-api/analysis.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

import { DocumentViewSection } from '../../../../providers/nlp-output.service';
import { ConfigurationLimits } from '../../pages/configuration-details/configuration-details.component';

interface TestDocument {
  id: string;
  text: string;
  hasBeenAnalyzed: boolean;
  analyses: DocumentAnalysis[];
}

interface IncludedOptions {
  id: string;
  label: string;
  isChecked: boolean;
  numUsed: number;
}
@EnableClassTelemetry()
@Component({
  selector: 'app-test-configuration-drawer',
  templateUrl: './test-configuration-drawer.component.html',
  styleUrls: ['./test-configuration-drawer.component.scss'],
  standalone: false,
})
export class TestConfigurationDrawerComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly open = input<boolean>();
  readonly configuration = input<SemantriaConfiguration>();
  readonly configurationLimits = input<ConfigurationLimits>();
  readonly close = output();
  @ViewChildren('documentViewer') documentViewer?: QueryList<ElementRef>;

  readonly options: DocumentViewOptions = {
    sentimentDisplay: 'numeric',
    sentimentRanges: {
      document: [
        { sentimentPolarity: 'negative', min: null, max: -0.1 },
        { sentimentPolarity: 'neutral', min: -0.1, max: 0.2 },
        { sentimentPolarity: 'positive', min: 0.2, max: null },
      ],
      feature: [
        { sentimentPolarity: 'negative', min: null, max: -0.45 },
        { sentimentPolarity: 'neutral', min: -0.45, max: 0.501 },
        { sentimentPolarity: 'positive', min: 0.501, max: null },
      ],
    },
    sentimentScale: 3,
  } as DocumentViewOptions;
  closedGroups: string[] = [];
  evaluationConfigId: string;
  includeCheckboxes: IncludedOptions[] = [];
  includedAnalyticItems: Array<string> = [];
  testDocuments: TestDocument[] = [];
  selectedDocument: TestDocument;
  evaluationInProgress = false;
  testDocInputValid = false;
  checkboxesValid = true;
  savedCheckboxValues = [];
  taxonomyArr = [];
  drawerOpen = false;

  public documentDescription = '';
  docTextInputChange$ = new Subject();
  constructor(
    private evaluationService: EvaluationService,
    private configurationService: ConfigurationService,
    private dialogService: DialogService,
    private alertService: AlertService,
    private router: Router,
    private groupService: GroupService
  ) {}

  ngOnInit(): void {
    this.docTextInputChange$.pipe(debounceTime(300)).subscribe((doc: any) => {
      this.checkDocInputValidity(doc.index, doc.event.target.textContent);
    });
  }

  onFocusOut(event, index: number) {
    if (this.testDocuments[index]) {
      this.testDocuments[index].text = event.target.textContent;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    //If drawer opened and configurationLimits present
    if (changes.open && changes.open.currentValue === true && this.configurationLimits()) {
      this.drawerOpen = true;
      if (this.testDocuments.length < 1) {
        this.addDocument(1000);
      }
      this.selectedDocument = this.testDocuments[0];
      this.includeCheckboxes = [
        {
          id: 'sentimentPolarity',
          label: translate('test-configuration-drawer-component.sentiment', {
            used: this.configurationLimits().sentiment.used,
          }),
          isChecked: true,
          numUsed: this.configurationLimits().sentiment.used,
        },
        {
          id: 'taxonomy',
          label: translate('test-configuration-drawer-component.taxonomy', {
            used: this.configurationLimits().taxonomy.used,
          }),
          isChecked: true,
          numUsed: this.configurationLimits().taxonomy.used,
        },
        {
          id: 'queryTopics',
          label: translate('test-configuration-drawer-component.query-topics', {
            used: this.configurationLimits().queries.used,
          }),
          isChecked: true,
          numUsed: this.configurationLimits().queries.used,
        },
        {
          id: 'conceptTopics',
          label: translate('test-configuration-drawer-component.concept-topics', {
            used: this.configurationLimits().concept_topics.used,
          }),
          isChecked: true,
          numUsed: this.configurationLimits().concept_topics.used,
        },
        {
          id: 'entities',
          label: translate('test-configuration-drawer-component.entities', {
            used: this.configurationLimits().entities.used,
          }),
          isChecked: true,
          numUsed: this.configurationLimits().entities.used,
        },
      ];

      //Disable checkboxes with no config items && retrieve saved checkbox states
      if (this.savedCheckboxValues.length === this.includeCheckboxes.length) {
        for (let i = 0; i < this.includeCheckboxes.length; i++) {
          this.includeCheckboxes[i].numUsed < 1
            ? (this.includeCheckboxes[i].isChecked = false)
            : (this.includeCheckboxes[i].isChecked = this.savedCheckboxValues[i]);
        }
        this.includeCheckboxes = Object.assign([], this.includeCheckboxes);
      } else {
        this.initializeCheckBoxes();
      }
      this.focusDocument(1000);
    }
    //If configuration change and configuration value exists, get config id
    if (changes.configuration && changes.configuration.currentValue) {
      if (this.configuration()) {
        this.getConfigID();
      }
    }
  }

  private async getConfigID() {
    this.evaluationConfigId = await this.configurationService.getEvaluationConfigId(
      this.configuration()
    );
  }

  //Check all enabled checkboxes
  private initializeCheckBoxes() {
    for (const box of this.includeCheckboxes) {
      box.numUsed < 1 ? (box.isChecked = false) : (box.isChecked = true);
    }
    this.includeCheckboxes = Object.assign([], this.includeCheckboxes);
  }

  public addDocument(delay = 0, focus = true) {
    this.testDocuments.push({ text: '', id: UUIDv4(), hasBeenAnalyzed: false, analyses: null });
    this.testDocInputValid = false;
    if (focus) {
      this.focusDocument(delay);
    }
  }

  focusDocument(delay = 0) {
    setTimeout(() => {
      const documentDiv = document.getElementById(
        'highlighted-test-doc-' + (this.testDocuments.length - 1)
      );
      documentDiv.focus();
    }, delay);
  }

  public removeDocument(index: number) {
    this.testDocuments.splice(index, 1);
    if (this.testDocuments.length === 0) {
      this.addDocument();
    } else {
      this.checkDocInputValidity();
      this.focusDocument();
    }
  }

  private checkDocInputValidity(index?: number, text?: string) {
    const docs = index > -1 ? this.testDocuments.filter((_, i) => i !== index) : this.testDocuments;

    const areOtherDocsEmpty = docs.some((doc) => doc.text.trim().length === 0);
    const isCurrentDocEmpty = text?.trim().length === 0;
    this.testDocInputValid = !(areOtherDocsEmpty || isCurrentDocEmpty);
  }

  public checkCheckboxValidity() {
    for (const box of this.includeCheckboxes) {
      if (box.isChecked === true) {
        this.checkboxesValid = true;
        return;
      }
    }
    this.checkboxesValid = false;
  }

  public onDocSelected(index: number) {
    this.selectedDocument = this.testDocuments[index];
  }

  public clearAll() {
    this.dialogService
      .showWarningPopup({
        title: translate('test-configuration-drawer-component.delete-question'),
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.testDocuments = [];
          this.savedCheckboxValues = [];
          this.addDocument();
          this.initializeCheckBoxes();
        }
      });
  }

  public async test(): Promise<void> {
    this.evaluationInProgress = true;
    try {
      this.includedAnalyticItems = [];
      for (const box of this.includeCheckboxes) {
        if (box.isChecked === true) {
          this.includedAnalyticItems.push(box.id);
        }
      }

      let docsToTest = [];
      for (const doc of this.testDocuments) {
        if (doc.hasBeenAnalyzed === false) {
          docsToTest.push(doc);
        }
      }
      if (docsToTest.length === 0) {
        docsToTest = [this.selectedDocument];
      }

      const evaluatedDocs = await this.evaluationService.evaluateDocuments(
        this.evaluationConfigId,
        docsToTest
      );

      const analyses = this.convertPropertiesToCamelCase(evaluatedDocs);
      for (const analysis of analyses) {
        if (analysis.taxonomy) {
          for (const taxonomy of analysis.taxonomy) {
            this.getTaxonomyPath(taxonomy, '', '');
          }
          for (const item of this.taxonomyArr) {
            let path = item.path;
            path = path.substring(0, path.lastIndexOf('/'));
            item.path = path;
          }
          analysis.taxonomy = this.taxonomyArr;
          this.taxonomyArr = [];
        }
      }

      for (const doc of this.testDocuments) {
        doc.hasBeenAnalyzed = true;
        const foundAnalysesIndex = analyses.findIndex((item) => item.id === doc.id);
        if (foundAnalysesIndex !== -1) {
          doc.analyses = [analyses[foundAnalysesIndex]];
        }
      }

      const selectedDocument = this.testDocuments.find(
        (item) => item.id === this.selectedDocument.id
      );

      this.selectedDocument = {
        text: this.selectedDocument.text,
        id: this.selectedDocument.id,
        analyses: selectedDocument.analyses,
        hasBeenAnalyzed: true,
      };

      this.evaluationInProgress = false;
    } catch (e) {
      this.alertService.showErrorToast(
        translate('test-configuration-drawer-component.failed-to-eval'),
        e
      );
      this.evaluationInProgress = false;
    }
  }

  private getTaxonomyPath(taxonomy, divider: string, path: string) {
    path = path + divider + taxonomy.title;
    divider = ' / ';
    this.taxonomyArr.push({ title: taxonomy.title, path });
    if (taxonomy.elements && !taxonomy.leafNode) {
      for (const element of taxonomy.elements) {
        this.getTaxonomyPath(element, divider, path);
      }
    }
  }

  public onEntityToEditChange(data: { editorState: string; entity: any }) {
    const configItem = data.entity;
    let fragment = '';
    data.editorState === 'blacklist' ? (fragment = 'blacklist') : (fragment = 'entities');
    const queryParams = {} as any;
    if (configItem) {
      queryParams.itemName = configItem.title;
      queryParams.itemType = configItem.entityType;
      queryParams.entityType = configItem.type;
    }
    const configsUrl = this.router.createUrlTree(
      [
        'groups',
        this.groupService.getSelectedGroupId(true),
        'configurations',
        this.configuration().id,
      ],
      {
        queryParams,
        fragment,
      }
    );
    window.open(this.router.serializeUrl(configsUrl));
  }

  public onTopicToEditChange(data: { editorState: string; topic: any }) {
    const topic = data.topic;
    const queryParams = {} as any;
    let fragment = '';

    if (topic.type === 'auto') {
      queryParams.section = 'topics-section';
      fragment = 'settings';
    } else if (topic.type === 'concept') {
      if (topic.id) {
        queryParams.itemId = topic.id;
        queryParams.itemName = topic.title;
      }
      fragment = 'concept_topics';
    } else if (topic.type === 'query') {
      if (topic.id) {
        queryParams.itemId = topic.id;
        queryParams.itemName = topic.title;
      }
      fragment = 'queries';
    } else {
      fragment = topic.type;
    }
    const urlTree = this.router.createUrlTree(
      [
        'groups',
        this.groupService.getSelectedGroupId(true),
        'configurations',
        this.configuration().id,
      ],
      {
        queryParams,
        fragment,
      }
    );
    window.open(this.router.serializeUrl(urlTree));
  }

  public cancel(): void {
    this.dialogService
      .showWarningPopup({
        title: translate('test-configuration-drawer-component.cancel-question'),
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.initializeCheckBoxes();
          this.savedCheckboxValues = [];
          this.testDocuments = [];
          this.addDocument(null, false);
          this.drawerOpen = false;
          this.close.emit();
        }
      });
  }

  public save(): void {
    this.savedCheckboxValues = [];
    for (const checkbox of this.includeCheckboxes) {
      this.savedCheckboxValues.push(checkbox.isChecked);
    }
    this.drawerOpen = false;
    this.close.emit();
  }

  public onDocumentSectionsChange(documentSectionsChangeEvent: {
    sections: DocumentViewSection[];
    id: string;
  }) {
    const selectedDocIndex = this.testDocuments.findIndex(
      (doc) => doc.id === documentSectionsChangeEvent.id
    );
    const documentDiv = document.getElementById('highlighted-test-doc-' + selectedDocIndex);
    this.documentDescription = documentSectionsChangeEvent.sections[0].value;
    documentDiv.innerHTML = this.documentDescription;
    const windowToScroll = this.documentViewer.filter(
      (element, index) => index === selectedDocIndex
    );
    if (windowToScroll[0]) {
      setTimeout(() => {
        const element = windowToScroll[0].nativeElement.getElementsByClassName('highlight')[0];
        element?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }

  private convertPropertiesToCamelCase(obj: any) {
    if (Array.isArray(obj)) {
      return obj.map((v) => this.convertPropertiesToCamelCase(v));
    } else if (obj != null && obj.constructor === Object) {
      return Object.keys(obj).reduce(
        (result, key) => ({
          ...result,
          [camelCase(key)]: this.convertPropertiesToCamelCase(obj[key]),
        }),
        {}
      );
    }
    return obj;
  }

  //Run to clear the highlights when the text is altered in the document.
  public clearSpans($event, document: TestDocument) {
    if (document.hasBeenAnalyzed) {
      document.text = $event.target.textContent;
      const currentCursor = this.getCursorCharacterOffsetWithin($event.target);
      this.clearTestDocumentSpan($event.target);
      setTimeout(() => {
        this.setCursor($event.target, currentCursor);
      }, 50);
      document.hasBeenAnalyzed = false;
    }
  }

  //Captures the current cursor location so that when we clear the spans
  //We can go back there.
  private getCursorCharacterOffsetWithin(element) {
    let caretOffset = 0;
    const doc = element.ownerDocument || element.document;
    const win = doc.defaultView || doc.parentWindow;
    if (typeof win.getSelection !== 'undefined') {
      const sel = win.getSelection();
      if (sel.rangeCount > 0) {
        const range = win.getSelection().getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        caretOffset = preCaretRange.toString().length;
      }
    }
    return caretOffset;
  }

  private setCursor(el, position) {
    const range = document.createRange();
    const sel = window.getSelection();
    const childNodeIdx = el.childNodes.length - 1;
    range.setStart(
      el.childNodes[childNodeIdx],
      Math.min(position, el.childNodes[childNodeIdx].length)
    );
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    const firstRange = sel.getRangeAt(0);
    const tempAnchorEl = document.createElement('br');
    firstRange.insertNode(tempAnchorEl);
    tempAnchorEl.scrollIntoView();
    tempAnchorEl.remove();
    el.focus();
  }

  private clearTestDocumentSpan(documentDiv) {
    documentDiv.innerHTML = documentDiv.innerHTML.replace(/<\/?(span|em)[^>]*>/g, '');
  }

  ngOnDestroy(): void {
    this.docTextInputChange$.unsubscribe();
  }
}
